import { useState, useMemo, useEffect, Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { MainOutletSkeleton } from "@/components/skeletons";
import AppSidebar from "./AppSidebar";
import StudioSwitcher from "./StudioSwitcher";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { HeaderRefreshProvider, useHeaderRefresh } from "@/contexts/HeaderRefreshContext";
import { AgentTracePanelHost } from "@/components/layout/AgentTracePanelHost";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { appLayoutMainColumnClassName } from "@/lib/app-layout-shell";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslatedVerticalLabels } from "@/lib/i18n/use-translated-vertical-labels";
import { HeaderChannelConnectActions, useChannelOAuthReturn } from "@/components/channel-connect";

const ROUTE_TITLE_KEYS: Record<string, string> = {
  "/": "nav.dashboard",
  "/dashboard": "nav.dashboard",
  "/chats": "nav.chats",
  "/archive": "nav.archive",
  "/settings": "nav.settings",
};

type AppHeaderPage = { kind: "i18n"; key: string } | { kind: "literal"; text: string };

function getPageForPath(pathname: string, dataSectionTitle: string): AppHeaderPage | null {
  const base = pathname.split("/").filter(Boolean)[0];
  const path = base ? `/${base}` : "/";
  if (path === "/studio-data" || path === "/property-data") {
    return { kind: "literal", text: dataSectionTitle };
  }
  const key = ROUTE_TITLE_KEYS[path];
  return key ? { kind: "i18n", key } : null;
}

function formatLastUpdated(ms: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  const mins = Math.floor((Date.now() - ms) / 60_000);
  if (mins < 1) return t("layout.lastUpdatedJustNow");
  if (mins === 1) return t("layout.lastUpdatedOneMin");
  return t("layout.lastUpdatedMins", { count: mins });
}

function AppLayoutHeader() {
  const { t } = useTranslation();
  useChannelOAuthReturn();
  const location = useLocation();
  const { user } = useAuth();
  const labels = useTranslatedVerticalLabels(user?.accountVertical);
  const header = useHeaderRefresh();
  const { refreshState, loading, lastRefreshedAt, runRefresh, statusText, beforeRefresh } = header ?? {};
  /** Scope hint: show only on pointer hover — Radix Tooltip also opens on focus/focus-within, which felt like a stray “info chip”. */
  const [scopeDescriptionOpen, setScopeDescriptionOpen] = useState(false);
  const page = useMemo(
    () => getPageForPath(location.pathname, labels.dataSectionNavLabel),
    [location.pathname, labels.dataSectionNavLabel],
  );
  const pageTitle = page ? (page.kind === "i18n" ? t(page.key) : page.text) : "";
  const isDashboard = location.pathname === "/dashboard" || location.pathname === "/";
  const isChats = /^\/chats(\/|$)/.test(location.pathname);
  const showHeaderStudioSwitcher = isDashboard || isChats;
  const showRefresh = refreshState && !location.pathname.startsWith("/settings");
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<string | null>(null);

  useEffect(() => {
    if (lastRefreshedAt == null) {
      setLastUpdatedLabel(null);
      return;
    }
    setLastUpdatedLabel(formatLastUpdated(lastRefreshedAt, t));
    const interval = setInterval(() => setLastUpdatedLabel(formatLastUpdated(lastRefreshedAt, t)), 60_000);
    return () => clearInterval(interval);
  }, [lastRefreshedAt, t]);

  const refreshCaption = statusText ?? lastUpdatedLabel;

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-black/[0.08] bg-card/80 px-6 backdrop-blur-sm">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          className={cn(
            "font-display text-[17.6px] font-semibold leading-[1.3] tracking-tight text-foreground truncate",
            showHeaderStudioSwitcher ? "inline-block shrink-0" : "hidden md:inline-block"
          )}
        >
          {pageTitle}
        </span>
        {showHeaderStudioSwitcher && pageTitle ? (
          <Tooltip
            open={scopeDescriptionOpen}
            onOpenChange={(next) => {
              if (!next) setScopeDescriptionOpen(false);
            }}
            delayDuration={400}
          >
            <TooltipTrigger asChild>
              <div
                className="min-w-0 shrink"
                onPointerEnter={() => setScopeDescriptionOpen(true)}
                onPointerLeave={() => setScopeDescriptionOpen(false)}
              >
                <StudioSwitcher />
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start" className="max-w-xs text-[12px] leading-snug">
              {labels.pageScopeDescription}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <HeaderChannelConnectActions />
        {showRefresh && (
          <div className="flex items-center gap-2">
            {beforeRefresh}
            {refreshCaption && (
              <span className="hidden max-w-[min(100vw-280px,420px)] truncate text-[11px] leading-5 text-muted-foreground sm:inline">
                {refreshCaption}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-2 text-[12px] leading-5"
              onClick={() => void runRefresh?.()}
              disabled={loading}
            >
              <RefreshCw className={cn("h-4 w-4 shrink-0", loading && "animate-spin")} />
              {loading ? t("layout.refreshing") : t("layout.refresh")}
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}

/** Same top-level route as `path="/chats/:conversationId?"` — must not change when only the id segment updates or Chats remounts and wipes local state (search, filters UI). */
function routeErrorBoundaryOutletKey(pathname: string): string {
  if (pathname === "/chats" || pathname.startsWith("/chats/")) return "/chats";
  return pathname;
}

export default function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { pathname } = useLocation();
  const routeOutletKey = routeErrorBoundaryOutletKey(pathname);

  return (
    <HeaderRefreshProvider>
      <AgentTracePanelHost />
      <div className="flex min-h-screen w-full overflow-x-hidden bg-background">
        <AppSidebar collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />

        <div
          className={appLayoutMainColumnClassName(sidebarCollapsed)}
          data-testid="app-layout-main-column"
        >
          <AppLayoutHeader />
          <main className="flex-1 min-w-0 px-6 pt-10 pb-6">
            {/* key: remount boundary per route so a prior page error does not block the next page */}
            <RouteErrorBoundary key={routeOutletKey}>
              <Suspense fallback={<MainOutletSkeleton />}>
                <Outlet />
              </Suspense>
            </RouteErrorBoundary>
          </main>
        </div>
      </div>
    </HeaderRefreshProvider>
  );
}
